import {
    Form,
    Container,
    Row,
    Col,
    Spinner,
    Alert,
    Button,
    Modal,
    Tab,
} from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import React, { useState, useEffect, useCallback } from 'react';

import { useAuth } from '@/shared/contexts/AuthContext';
import { useAuthenticatedAxios } from '@/shared/services/axiosInstance';
import { getCommunity, deleteCommunity } from '@/shared/services/communityApi';
import {
    getPosts,
    createPost,
    deletePost,
    pinPost,
} from '@/shared/services/postApi';
import {
    getMemberships,
    checkMembership,
    joinCommunity,
    leaveCommunity,
    banMember,
    unbanMember,
    submitModeratorApplication,
} from '@/shared/services/membershipApi';
import { formatRelativeTime, formatFullDate } from '@/shared/utils/dateUtils';
import SEO from '@/components/common/SEO';
import InfoTooltip from '@/components/common/InfoTooltip';

import usePostEngagement from './hooks/usePostEngagement';
import CommunityHero from './components/CommunityHero';
import CommunityHeader from './components/CommunityHeader';
import CommunityTabs from './components/CommunityTabs';
import PostCard from './components/PostCard';
import CreatePostModal from './components/CreatePostModal';
import ApplyToModerateModal from './components/ApplyToModerateModal';
import DeleteCommunityModal from './components/DeleteCommunityModal';
import ReportModal from './components/ReportModal';
import GuidelinesContent from './components/GuidelinesContent';
import MembersGrid from './components/MembersGrid';
import CommunitySearch from './components/CommunitySearch';
import './Communities.css';

const CommunityPage = () => {
    const { slug } = useParams();
    const { user, isAdmin } = useAuth();
    const axiosInstance = useAuthenticatedAxios();
    const navigate = useNavigate();

    // State
    const [activeTab, setActiveTab] = useState('posts');
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [community, setCommunity] = useState(null);
    const [posts, setPosts] = useState([]);
    const [postErrors, setPostErrors] = useState({});
    const [isMember, setIsMember] = useState(false);
    const [isModerator, setIsModerator] = useState(isAdmin);
    const [showCreatePostModal, setShowCreatePostModal] = useState(false);
    const [showReportModal, setShowReportModal] = useState(false);
    const [confirmBanMemberId, setConfirmBanMemberId] = useState(null);
    const [confirmUnbanMember, setConfirmUnbanMember] = useState(null);
    const [banReason, setBanReason] = useState('');
    const [reportingPostId, setReportingPostId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [sortBy, setSortBy] = useState('new');
    const [members, setMembers] = useState([]);
    const [membersLoading, setMembersLoading] = useState(false);
    const [membersError, setMembersError] = useState(null);
    const [allMembersFetched, setAllMembersFetched] = useState(false);
    const [offset, setOffset] = useState(4);
    const [flashMessage, setFlashMessage] = useState(null);
    const [confirmDeletePostId, setConfirmDeletePostId] = useState(null);
    const [fetchedForCommunityId, setFetchedForCommunityId] = useState(null);
    const [currentUserMongoId, setCurrentUserMongoId] = useState(null);
    const [eventPosts, setEventPosts] = useState([]);
    const [eventsLoading, setEventsLoading] = useState(false);
    const [eventsFetched, setEventsFetched] = useState(false);
    const [canApplyToModerate, setCanApplyToModerate] = useState(false);
    const [membershipId, setMembershipId] = useState(null);
    const [showApplyModal, setShowApplyModal] = useState(false);
    const [applying, setApplying] = useState(false);
    const [applyError, setApplyError] = useState(null);
    const [latestApplication, setLatestApplication] = useState(null);

    const handleReaction = useCallback(
        (postId, counts) =>
            setPosts((prev) =>
                prev.map((p) => (p._id === postId ? { ...p, ...counts } : p))
            ),
        [setPosts]
    );

    const handleReactionError = useCallback((message, postId) => {
        setPostErrors((prev) => ({ ...prev, [postId]: message }));
    }, []);

    const {
        engagements: postEngagements,
        fetchEngagements,
        handleLike: handleLikeEngagement,
        handleDislike: handleDislikeEngagement,
        handleBookmark: handleBookmarkEngagement,
    } = usePostEngagement(axiosInstance, {
        onReaction: handleReaction,
        onError: handleReactionError,
    });

    // Fetch community data
    const fetchCommunity = useCallback(async () => {
        try {
            // TODO: Use slug from URL params when routing is set up
            const communitySlug = slug || 'urban-gardening';
            const data = await getCommunity(communitySlug);
            setCommunity(data);
            return data;
        } catch (err) {
            console.error('Error fetching community:', err);
            setError('Failed to load community');
            return null;
        }
    }, [slug]);

    // Fetch posts for community.
    // sortBy is passed in as an argument rather than captured from state so
    // this callback stays stable. That keeps a sort change from invalidating
    // the initial-load effect (which would otherwise re-fetch the community
    // and membership as well as the posts).
    const fetchPosts = useCallback(async (communityId, sort) => {
        try {
            const data = await getPosts({
                communityId,
                sortBy: sort,
            });
            setPosts(data);
        } catch (err) {
            console.error('Error fetching posts:', err);
        }
    }, []);

    // Fetch event posts for community (lazy-loaded on first tab selection)
    const fetchEventPosts = useCallback(async (communityId) => {
        setEventsLoading(true);
        try {
            const data = await getPosts({
                communityId,
                postType: 'event',
            });
            setEventPosts(data);
            setEventsFetched(true);
        } catch (err) {
            console.error('Error fetching event posts:', err);
        } finally {
            setEventsLoading(false);
        }
    }, []);

    // Check membership status
    const fetchMembershipStatus = useCallback(
        async (communityId) => {
            if (!user || !axiosInstance) return;
            try {
                const data = await checkMembership(axiosInstance, communityId);
                setIsMember(data.isMember);
                const role = data.membership?.role;
                setIsModerator(
                    isAdmin || role === 'moderator' || role === 'admin'
                );
                setCurrentUserMongoId(data.membership?.userId || null);
                setCanApplyToModerate(!!data.canApplyToModerate);
                setMembershipId(data.membership?._id || null);

                // Find the most recent resolved application (accepted or
                // rejected) so the header can show the outcome to the user.
                const apps = data.membership?.moderatorApplications || [];
                const resolved = apps
                    .filter(
                        (a) =>
                            a.status === 'accepted' || a.status === 'rejected'
                    )
                    .sort(
                        (a, b) =>
                            new Date(b.resolvedAt) - new Date(a.resolvedAt)
                    );
                setLatestApplication(resolved[0] || null);
            } catch (err) {
                console.error('Error checking membership:', err);
            }
        },
        [user, axiosInstance, isAdmin]
    );

    // Fetch community members
    const fetchMembers = useCallback(
        async (communityId, limit, offset) => {
            if (!communityId || allMembersFetched) return;

            setMembersLoading(true);
            setMembersError(null);

            try {
                // Fetch members with your API
                const data = await getMemberships(
                    {
                        communityId,
                        limit,
                        offset,
                    },
                    axiosInstance
                );

                // Append new members to the existing list
                setMembers((prev) => [...prev, ...data]);

                // If backend returned fewer than requested, no more members left
                if (data.length < limit) {
                    setAllMembersFetched(true);
                }
            } catch (err) {
                console.error('Error fetching members:', err);
                setMembersError('Failed to load members');
            } finally {
                setMembersLoading(false);
            }
        },
        [allMembersFetched, axiosInstance]
    );

    // Initial data fetch: community details + membership status.
    // Posts are fetched by the separate sort-aware effect below, so changing
    // the sort order does not re-request the community or membership.
    useEffect(() => {
        const loadData = async () => {
            setLoading(true);
            const communityData = await fetchCommunity();
            if (communityData) {
                await fetchMembershipStatus(communityData._id);
            }
            setLoading(false);
        };
        loadData();
    }, [fetchCommunity, fetchMembershipStatus]);

    // Fetch posts whenever the community resolves or the sort order changes.
    // This is the only path that re-requests posts on a sort change.
    useEffect(() => {
        if (community?._id) {
            fetchPosts(community._id, sortBy);
        }
    }, [community?._id, sortBy, fetchPosts]);

    // Fetch engagements once per community — keyed on community._id so that
    // a stale (community, posts) pair from the previous community cannot
    // consume the gate before both states have updated to the new community
    useEffect(() => {
        if (
            posts.length > 0 &&
            user &&
            community?._id &&
            posts[0]?.communityId === community._id &&
            fetchedForCommunityId !== community._id
        ) {
            fetchEngagements(posts);
            setFetchedForCommunityId(community._id);
        }
    }, [posts, user, fetchEngagements, fetchedForCommunityId, community]);

    // Fetch event posts when Events tab is first selected
    useEffect(() => {
        if (activeTab === 'events' && community?._id && !eventsFetched) {
            fetchEventPosts(community._id);
        }
    }, [activeTab, community, fetchEventPosts, eventsFetched]);

    // Fetch members when Members tab is selected
    useEffect(() => {
        if (activeTab === 'members' && community?._id && members.length === 0) {
            fetchMembers(community._id, 4);
        }
    }, [activeTab, community, fetchMembers, members.length]);

    // Handle join/leave community
    const handleJoinClick = async () => {
        try {
            if (isMember) {
                await leaveCommunity(axiosInstance, community._id);
                setIsMember(false);
                setCommunity((prev) => ({
                    ...prev,
                    memberCount: prev.memberCount - 1,
                }));
                setAllMembersFetched(false);
                setMembers([]); // Clear stale data
                setOffset(4);
                // No longer a member: hide the Apply to Moderate button
                // and the application outcome indicator.
                setCanApplyToModerate(false);
                setMembershipId(null);
                setLatestApplication(null);
            } else {
                await joinCommunity(axiosInstance, community._id);
                setIsMember(true);
                setCommunity((prev) => ({
                    ...prev,
                    memberCount: prev.memberCount + 1,
                }));
                setAllMembersFetched(false);
                setMembers([]); // Clear stale data
                setOffset(4);
                // Refresh only the moderator-eligibility fields so the Apply to
                // Moderate button (and the membershipId used to submit) update
                // without a page reload. Deliberately does NOT touch isMember,
                // so the optimistic join above stays instant.
                try {
                    const data = await checkMembership(
                        axiosInstance,
                        community._id
                    );
                    setCanApplyToModerate(!!data?.canApplyToModerate);
                    setMembershipId(data?.membership?._id || null);
                } catch (err) {
                    console.error(
                        'Error refreshing moderator eligibility:',
                        err
                    );
                }
            }
        } catch (err) {
            console.error('Error updating membership:', err);
            const backendMessage = err.response?.data?.message;
            setFlashMessage({
                variant: 'danger',
                text:
                    backendMessage ||
                    'Failed to update membership. Please try again.',
            });
        }
    };

    // Handle create post
    const handleMakePost = () => {
        if (!user) {
            setFlashMessage({
                variant: 'warning',
                text: 'Please log in to create a post.',
            });
            return;
        }
        setShowCreatePostModal(true);
    };

    const handleOpenApply = () => {
        setApplyError(null);
        setShowApplyModal(true);
    };

    const handleSubmitApplication = async (applicationText) => {
        setApplying(true);
        setApplyError(null);
        try {
            await submitModeratorApplication(
                axiosInstance,
                membershipId,
                applicationText
            );
            setCanApplyToModerate(false);
            setShowApplyModal(false);
            setFlashMessage({
                variant: 'success',
                text: 'Your application to moderate has been submitted successfully.',
            });
        } catch (err) {
            const status = err.response?.status;
            const apiMsg = err.response?.data?.message;
            if (status === 409) {
                // Duplicate: the user already has a pending application.
                setApplyError(
                    apiMsg || 'You already have a pending application.'
                );
            } else if (status === 429) {
                // Cooldown: the server does not emit 429 today (no cooldown
                // logic yet), but this branch is ready if/when it does.
                setApplyError(
                    apiMsg ||
                        'You have applied recently. Please wait before reapplying.'
                );
            } else {
                setApplyError(
                    apiMsg || 'Something went wrong. Please try again.'
                );
            }
        } finally {
            setApplying(false);
        }
    };

    const handleCreatePost = async (postData) => {
        try {
            const newPost = await createPost(
                axiosInstance,
                {
                    communityId: community._id,
                    postType: postData.postType || 'discussion',
                    title: postData.title || '',
                    content: postData.content,
                    resourceUrl: postData.resourceUrl || '',
                    resourceType: postData.resourceType || '',
                    eventDate: postData.eventDate || '',
                    eventEndDate: postData.eventEndDate || '',
                    eventLocation: postData.eventLocation || '',
                    eventRegistrationUrl: postData.eventRegistrationUrl || '',
                },
                postData.image || null
            );

            if (newPost.moderationStatus === 'published') {
                // Add published posts to the top of the feed immediately
                setPosts((prevPosts) => [newPost, ...prevPosts]);
                if (newPost.postType === 'event') {
                    setEventPosts((prev) => [newPost, ...prev]);
                }
            }
            // 'blocked' and 'pending_review' are not added to the feed — the modal handles user feedback

            return newPost;
        } catch (err) {
            console.error('Error creating post:', err);
            throw err; // Re-throw actual network/server errors to the modal
        }
    };

    const handleViewGuidelines = () => {
        setShowCreatePostModal(false);
        setActiveTab('guidelines');
    };

    const handleLike = (postId) => {
        if (!user) {
            setFlashMessage({
                variant: 'warning',
                text: 'Please log in to like posts.',
            });
            return;
        }
        handleLikeEngagement(postId);
    };

    const handleDislike = (postId) => {
        if (!user) {
            setFlashMessage({
                variant: 'warning',
                text: 'Please log in to dislike posts.',
            });
            return;
        }
        handleDislikeEngagement(postId);
    };

    const handleBookmark = (postId) => {
        if (!user) {
            setFlashMessage({
                variant: 'warning',
                text: 'Please log in to bookmark posts.',
            });
            return;
        }
        handleBookmarkEngagement(postId);
    };

    // Handle report - opens the report modal
    const handleReportPost = (postId) => {
        if (!user) {
            setFlashMessage({
                variant: 'warning',
                text: 'Please log in to report posts.',
            });
            return;
        }

        setReportingPostId(postId);
        setShowReportModal(true);
    };

    // Handle successful report submission
    const handleReportSuccess = () => {
        setPosts((prevPosts) =>
            prevPosts.map((post) => {
                if (post._id === reportingPostId) {
                    // Increment report count
                    const newReportCount = (post.reportCount || 0) + 1;
                    return { ...post, reportCount: newReportCount };
                }
                return post;
            })
        );
    };

    // Handle delete post — opens confirm modal instead of window.confirm
    const handleDeletePost = (postId) => {
        if (!user) {
            setFlashMessage({
                variant: 'warning',
                text: 'Please log in to delete posts.',
            });
            return;
        }
        setConfirmDeletePostId(postId);
    };

    const confirmDeletePost = async () => {
        const postId = confirmDeletePostId;
        setConfirmDeletePostId(null);
        try {
            const deletedPost = await deletePost(axiosInstance, postId);
            setPosts((prevPosts) =>
                prevPosts.flatMap((post) => {
                    if (post._id !== postId) return [post];
                    if ((deletedPost.commentCount || 0) === 0) return [];
                    return [{ ...post, ...deletedPost }];
                })
            );
            setFlashMessage({
                variant: 'success',
                text: 'Post deleted successfully.',
            });
        } catch (err) {
            console.error('Error deleting post:', err);
            const backendMessage = err.response?.data?.message;
            setFlashMessage({
                variant: 'danger',
                text:
                    backendMessage ||
                    'Failed to delete post. Please try again.',
            });
        }
    };

    // Handle pin/unpin post
    const handlePinPost = async (postId, currentlyPinned) => {
        try {
            const updatedPost = await pinPost(
                axiosInstance,
                postId,
                !currentlyPinned
            );
            setPosts((prevPosts) => {
                const next = prevPosts.map((post) =>
                    post._id === postId
                        ? {
                              ...post,
                              pinned: updatedPost.pinned,
                              pinnedAt: updatedPost.pinnedAt,
                          }
                        : post
                );
                // Re-sort: pinned posts first, then by original order
                return [
                    ...next.filter((p) => p.pinned),
                    ...next.filter((p) => !p.pinned),
                ];
            });
        } catch (err) {
            const backendMessage = err.response?.data?.message;
            setFlashMessage({
                variant: 'danger',
                text:
                    backendMessage ||
                    'Failed to update pin status. Please try again.',
            });
        }
    };

    const handleLoadMoreMembers = async () => {
        if (!community?._id || allMembersFetched) return;

        try {
            // Fetch members using current offset
            await fetchMembers(community._id, 4, offset); // pass offset as argument

            // Update offset for next fetch
            setOffset((prevOffset) => prevOffset + 4);
        } catch (err) {
            console.error('Error loading more members', err);
        }
    };

    // Handle comment (placeholder)
    const handleComment = () => {
        // TODO: Open comment modal or navigate to post detail
    };

    const handleBan = async (membershipId) => {
        setConfirmBanMemberId(membershipId);
    };

    const confirmBan = async () => {
        const membershipId = confirmBanMemberId;
        setConfirmBanMemberId(null);
        try {
            const data = await banMember(
                axiosInstance,
                membershipId,
                banReason
            );

            // Merge the server's audit fields (status, bannedBy, bannedReason,
            // bannedAt) into local state so the unban modal can display them
            // without a reload. Fall back to just flipping status if an older
            // server response omits the members
            const auditFields = data?.membership ?? { status: 'banned' };
            setMembers((prev) =>
                prev.map((m) =>
                    m._id === membershipId ? { ...m, ...auditFields } : m
                )
            );

            setCommunity((prev) => ({
                ...prev,
                memberCount: prev.memberCount - 1,
            }));
            setFlashMessage({
                variant: 'success',
                text: 'Member banned successfully.',
            });
        } catch (err) {
            const backendMessage = err.response?.data?.message;
            setFlashMessage({
                variant: 'danger',
                text: backendMessage || 'Failed to ban member.',
            });
        }
        setBanReason('');
    };

    const handleUnban = async (membership) => {
        setConfirmUnbanMember(membership);
    };

    const confirmUnban = async () => {
        const membership = confirmUnbanMember;
        setConfirmUnbanMember(null);
        try {
            await unbanMember(axiosInstance, membership._id);
            setMembers((prev) =>
                prev.map((m) =>
                    m._id === membership._id ? { ...m, status: 'active' } : m
                )
            );
            setCommunity((prev) => ({
                ...prev,
                memberCount: prev.memberCount + 1,
            }));
            setFlashMessage({
                variant: 'success',
                text: 'Member unbanned successfully.',
            });
        } catch (err) {
            const backendMessage = err.response?.data?.message;
            setFlashMessage({
                variant: 'danger',
                text: backendMessage || 'Failed to unban member.',
            });
        }
    };

    const handleSearch = useCallback(
        async (search) => {
            if (!community?._id) return;

            // handlers support easy addition of search fields
            const handlers = {
                posts: async () => {
                    const data = await getPosts({
                        communityId: community._id,
                        sortBy,
                        search,
                    });
                    setPosts(data);
                },
            };

            try {
                const fn = handlers[activeTab];
                if (!fn) return;

                await fn();
            } catch (err) {
                console.error(`Error fetching ${activeTab}:`, err);
            }
        },
        [community?._id, sortBy, activeTab]
    );
    // Handle delete community
    const handleDeleteCommunity = async () => {
        await deleteCommunity(axiosInstance, community._id);
        navigate('/communities');
    };

    // Loading state
    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center py-5">
                <Spinner animation="border" variant="primary" />
                <span className="visually-hidden">Loading...</span>
            </div>
        );
    }

    // Error state
    if (error) {
        return (
            <Container className="py-5">
                <Alert variant="danger">{error}</Alert>
            </Container>
        );
    }

    // No community found
    if (!community) {
        return (
            <Container className="py-5">
                <Alert variant="warning">Community not found</Alert>
            </Container>
        );
    }

    const renderPostCard = (post) => (
        <PostCard
            key={post._id}
            post={{
                postId: post._id,
                authorUsername: post.authorName,
                communityName: community.name,
                postType: post.postType,
                title: post.title,
                userName: post.authorName,
                userProfilePic: post.authorProfilePic,
                userDate: formatRelativeTime(post.createdAt),
                userFullDate: formatFullDate(post.createdAt),
                content: post.content,
                mediaUrl: post.mediaUrl,
                resourceUrl: post.resourceUrl,
                resourceType: post.resourceType,
                eventDate: post.eventDate,
                eventEndDate: post.eventEndDate,
                eventLocation: post.eventLocation,
                eventRegistrationUrl: post.eventRegistrationUrl,
                likes: post.likeCount,
                dislikes: post.dislikeCount,
                comments: post.commentCount,
                shares: post.shareCount,
                isPinned: post.pinned || false,
                slug,
                status: post.status,
                deletedByRole: post.deletedByRole,
                flashMessage: postErrors[post._id] || null,
            }}
            engagement={{
                reaction: postEngagements[post._id]?.reaction || null,
                isBookmarked: postEngagements[post._id]?.isBookmarked || false,
            }}
            handlers={{
                onLike: () => handleLike(post._id),
                onDislike: () => handleDislike(post._id),
                onComment: () => handleComment(post._id),
                onBookmark: () => handleBookmark(post._id),
                onReport: () => handleReportPost(post._id),
                onDelete: () => handleDeletePost(post._id),
                onPin: () => handlePinPost(post._id, post.pinned),
                onDismissFlash: () =>
                    setPostErrors((prev) => ({
                        ...prev,
                        [post._id]: null,
                    })),
            }}
            permissions={{
                isModerator,
                canDelete: !!user && (post.isMine || isModerator || isAdmin),
                user,
            }}
        />
    );

    return (
        <div className="community-page">
            <SEO title={community.name} />
            {/* Flash message (replaces window.alert) */}
            {flashMessage && (
                <Container className="mt-3">
                    <Alert
                        variant={flashMessage.variant}
                        dismissible
                        onClose={() => setFlashMessage(null)}
                    >
                        {flashMessage.text}
                    </Alert>
                </Container>
            )}

            {/* Hero Image */}
            <CommunityHero
                imageUrl={community.heroImage}
                name={community.name}
            />

            {/* Community Header */}
            <CommunityHeader
                icon={community.icon}
                title={community.name}
                subtitle={community.category}
                description={community.description}
                memberCount={community.memberCount?.toLocaleString() || '0'}
                onJoinClick={handleJoinClick}
                onMakePost={handleMakePost}
                isMember={isMember}
                isAdmin={isAdmin}
                user={user}
                onDeleteClick={() => setShowDeleteModal(true)}
                canApplyToModerate={canApplyToModerate}
                onApplyToModerate={handleOpenApply}
                latestApplication={latestApplication}
            />

            {/* Search */}
            <Container>
                <div className="row justify-content-center">
                    <div className="col-md-12 col-lg-10">
                        <CommunitySearch
                            onSearch={handleSearch}
                            placeholderText={`Search in ${activeTab}...`}
                        />
                    </div>
                </div>
            </Container>

            {/* Tabs */}
            <Container>
                <Row>
                    <Col lg={10} className="mx-auto">
                        {/*
                         * Tab.Container supplies the tab context that
                         * react-bootstrap's Nav needs for arrow-key navigation,
                         * plus the id / aria-controls / aria-labelledby wiring.
                         * transition={false} and unmountOnExit preserve the
                         * previous behaviour: no fade, only the active panel in
                         * the DOM.
                         */}
                        <Tab.Container
                            id="community-tabs"
                            activeKey={activeTab}
                            transition={false}
                            unmountOnExit
                        >
                            <CommunityTabs
                                activeTab={activeTab}
                                onTabChange={setActiveTab}
                            />

                            <Tab.Content>
                                {/* Posts Feed */}
                                <Tab.Pane eventKey="posts">
                                    <div className="post-actions mb-3">
                                        <div className="post-action-buttons">
                                            <div>
                                                <label htmlFor="sortSelect">
                                                    Sort by:{' '}
                                                </label>
                                                <Form.Select
                                                    id="sortSelect"
                                                    className="form-select"
                                                    value={sortBy}
                                                    onChange={(e) =>
                                                        setSortBy(
                                                            e.target.value
                                                        )
                                                    }
                                                >
                                                    <option value="new">
                                                        Newest
                                                    </option>
                                                    <option value="likes">
                                                        Most Liked
                                                    </option>
                                                </Form.Select>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="posts-feed">
                                        {posts.length === 0 ? (
                                            <div className="text-center py-5 text-muted">
                                                <p>
                                                    No posts yet. Be the first
                                                    to post!
                                                </p>
                                            </div>
                                        ) : (
                                            posts.map((post) =>
                                                // handlers - new object reference on every render is
                                                // acceptable while PostCard is not React.memo'd
                                                renderPostCard(post)
                                            )
                                        )}
                                    </div>
                                </Tab.Pane>

                                {/* Members Tab */}
                                <Tab.Pane
                                    eventKey="members"
                                    className="members-section"
                                >
                                    <MembersGrid
                                        members={members}
                                        loading={
                                            membersLoading &&
                                            members.length === 0
                                        }
                                        error={membersError}
                                        onBan={handleBan}
                                        onUnban={handleUnban}
                                        isSiteAdmin={isAdmin}
                                        isModeratorOfCommunity={isModerator}
                                        currentUserId={currentUserMongoId}
                                    />
                                    {!allMembersFetched &&
                                        members.length > 0 && (
                                            <Row className="py-5">
                                                <Col className="text-end">
                                                    <Button
                                                        variant="primary"
                                                        size="lg"
                                                        onClick={
                                                            handleLoadMoreMembers
                                                        }
                                                        disabled={
                                                            membersLoading ||
                                                            allMembersFetched
                                                        }
                                                    >
                                                        {membersLoading
                                                            ? 'Loading...'
                                                            : 'Load More'}
                                                    </Button>
                                                </Col>
                                            </Row>
                                        )}
                                </Tab.Pane>

                                <Tab.Pane
                                    eventKey="events"
                                    className="events-section"
                                >
                                    {eventsLoading ? (
                                        <div className="d-flex justify-content-center py-5">
                                            <Spinner
                                                animation="border"
                                                variant="primary"
                                            />
                                            <span className="visually-hidden">
                                                Loading events...
                                            </span>
                                        </div>
                                    ) : eventPosts.length === 0 ? (
                                        <div className="text-center py-5 text-muted">
                                            <i
                                                className="bi bi-calendar-event fs-1 d-block mb-3"
                                                aria-hidden="true"
                                            />
                                            <p>
                                                No events yet. Be the first to
                                                create one!
                                            </p>
                                        </div>
                                    ) : (
                                        <div className="posts-feed">
                                            {eventPosts.map(renderPostCard)}
                                        </div>
                                    )}
                                </Tab.Pane>

                                {/*
                                 * tabIndex on these two panels only: the ARIA
                                 * tabs pattern wants a tabpanel focusable when
                                 * it holds nothing focusable, otherwise
                                 * keyboard users tab straight past it.
                                 */}
                                <Tab.Pane
                                    eventKey="similar"
                                    className="similar-section py-5 text-center text-muted"
                                    tabIndex={0}
                                >
                                    <h4>Similar Communities</h4>
                                    <p>Coming soon...</p>
                                </Tab.Pane>

                                <Tab.Pane
                                    eventKey="guidelines"
                                    className="guidelines-section py-4"
                                    tabIndex={0}
                                >
                                    <GuidelinesContent />
                                </Tab.Pane>
                            </Tab.Content>
                        </Tab.Container>
                    </Col>
                </Row>

                {/* Back to Communities Button */}
                <Row className="py-5">
                    <Col className="text-center">
                        <Button
                            variant="primary"
                            size="lg"
                            onClick={() => navigate('/communities')}
                            aria-label="Back to Communities"
                        >
                            Back to Communities
                        </Button>
                    </Col>
                </Row>
            </Container>

            {/* Create Post Modal */}
            <CreatePostModal
                show={showCreatePostModal}
                onHide={() => setShowCreatePostModal(false)}
                onSubmit={handleCreatePost}
                onViewGuidelines={handleViewGuidelines}
            />

            {/* Apply to Moderate Modal */}
            <ApplyToModerateModal
                show={showApplyModal}
                onHide={() => setShowApplyModal(false)}
                onSubmit={handleSubmitApplication}
                submitting={applying}
                error={applyError}
            />

            {/* Delete Community Modal */}
            <DeleteCommunityModal
                show={showDeleteModal}
                onHide={() => setShowDeleteModal(false)}
                onConfirm={handleDeleteCommunity}
                communityName={community.name}
            />

            {/* Confirm Delete Post Modal (replaces window.confirm) */}
            <Modal
                show={!!confirmDeletePostId}
                onHide={() => setConfirmDeletePostId(null)}
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>Confirm Delete</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Are you sure you want to delete this post?
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="secondary"
                        onClick={() => setConfirmDeletePostId(null)}
                    >
                        Cancel
                    </Button>
                    <Button variant="danger" onClick={confirmDeletePost}>
                        Delete
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Report Post Modal */}
            <ReportModal
                show={showReportModal}
                onHide={() => {
                    setShowReportModal(false);
                    setReportingPostId(null);
                }}
                postId={reportingPostId || ''}
                communityId={community._id}
                axiosInstance={axiosInstance}
                onReportSuccess={handleReportSuccess}
            />

            {/* Confirm Ban User Modal */}
            <Modal
                show={!!confirmBanMemberId}
                onHide={() => {
                    setConfirmBanMemberId(null);
                    setBanReason('');
                }}
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>Confirm Ban</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Are you sure you want to <strong>ban</strong> this user?{' '}
                    <div className="mb-3">
                        <label
                            htmlFor="ban-reason"
                            className="form-label small fw-medium"
                        >
                            Ban reason{' '}
                            <span className="text-secondary">(optional)</span>
                        </label>
                        <InfoTooltip text="Only moderators and site admins can read this. It is kept as an internal record and is never shown to the banned member." />
                        <textarea
                            id="ban-reason"
                            className="form-control"
                            rows={3}
                            placeholder="Describe why this user is being banned..."
                            value={banReason}
                            onChange={(e) => setBanReason(e.target.value)}
                        />
                        <p className="text-danger small mt-3">
                            This action bans the user, restricting them from
                            posting or otherwise participating in this
                            community, until they are{' '}
                            <strong>manually unbanned.</strong>
                        </p>
                    </div>
                </Modal.Body>

                <Modal.Footer>
                    <Button
                        variant="secondary"
                        onClick={() => {
                            setConfirmBanMemberId(null);
                            setBanReason('');
                        }}
                    >
                        Cancel
                    </Button>
                    <Button variant="danger" onClick={confirmBan}>
                        Ban
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Confirm Unban User Modal */}
            <Modal
                show={!!confirmUnbanMember}
                onHide={() => {
                    setConfirmUnbanMember(null);
                }}
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>Confirm Unban</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Are you sure you want to <strong>unban</strong> this user?{' '}
                    <div className="mb-3">
                        <p className="small fw-medium mb-2">Ban Information</p>
                        <dl className="mb-0 small">
                            <dt className="fw-medium">Banned by</dt>
                            <dd className="text-muted">
                                {confirmUnbanMember?.bannedBy || 'Unknown'}
                            </dd>
                            <dt className="fw-medium">Reason</dt>
                            <dd className="text-muted">
                                {confirmUnbanMember?.bannedReason ||
                                    'No reason provided'}
                            </dd>
                            <dt className="fw-medium">Banned at</dt>
                            <dd className="text-muted">
                                {confirmUnbanMember?.bannedAt
                                    ? formatFullDate(
                                          confirmUnbanMember?.bannedAt
                                      )
                                    : 'Unknown'}
                            </dd>
                        </dl>
                        <p className="text-danger small mt-3">
                            This action unbans the user, allowing them to post
                            and otherwise participate in this community.
                        </p>
                    </div>
                </Modal.Body>

                <Modal.Footer>
                    <Button
                        variant="secondary"
                        onClick={() => {
                            setConfirmUnbanMember(null);
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="warning"
                        className="text-dark"
                        onClick={confirmUnban}
                    >
                        Unban
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default CommunityPage;
